import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { MapPin, Clock, Star, Award, CheckCircle, MessageSquare, Phone, Mail, Briefcase, X, ArrowLeft, Loader, Calendar } from 'lucide-react';
import Navigation from './Navigation';

const API_BASE = 'https://hive-backend-4gcf.onrender.com';

export default function ServiceDetailsPage() {
  const { id } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showContactModal, setShowContactModal] = useState(false);

  useEffect(() => {
    const fetchService = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/services/${id}`);
        const data = await res.json();
        if (res.ok && (data.service || data)) {
          setService(data.service || data);
        } else {
          setError('Service not found');
        }
      } catch (err) {
        setError('Failed to load service');
      } finally {
        setLoading(false);
      }
    };
    fetchService();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
        <Navigation />
        <div className="flex items-center justify-center pt-40">
          <Loader className="w-10 h-10 text-yellow-400 animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
        <Navigation />
        <div className="pt-24 pb-8 px-4">
          <div className="max-w-5xl mx-auto text-center">
            <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 p-12">
              <div className="text-6xl mb-4">😕</div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Service Not Found</h1>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                This service may have been removed or is no longer available.
              </p>
              <button onClick={() => window.location.href = '/'}
                className="px-6 py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white font-bold rounded-xl hover:shadow-2xl transition-all"
              >Back to Home</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const provider = service.providerId || service.provider || {};
  const providerName = provider.name || service.providerName || 'Provider';
  const providerPhone = service.phone || provider.phone || '';
  const providerEmail = service.email || provider.email || '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
      <Navigation />
      
      <div className="pt-24 pb-8 px-4">
        <div className="max-w-5xl mx-auto">

          <button onClick={() => window.history.back()}
            className="mb-6 flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-yellow-600 dark:hover:text-yellow-400 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back</span>
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Left Column */}
            <div className="lg:col-span-2 space-y-6">

              {/* Provider Card */}
              <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 p-6">
                <div className="flex items-start gap-4">
                  <div className="w-20 h-20 bg-gradient-to-br from-yellow-100 to-yellow-200 dark:from-yellow-900/30 dark:to-yellow-800/30 rounded-2xl flex items-center justify-center text-3xl font-black text-yellow-600 flex-shrink-0">
                    {providerName.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <h1 className="text-2xl font-black text-gray-900 dark:text-white mb-1">{providerName}</h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{service.title}</p>
                    <div className="flex items-center gap-4 flex-wrap">
                      {service.averageRating > 0 && (
                        <div className="flex items-center gap-1 bg-yellow-50 dark:bg-yellow-900/20 px-3 py-1.5 rounded-full">
                          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                          <span className="text-sm font-bold">{service.averageRating}</span>
                          {service.totalReviews > 0 && <span className="text-xs text-gray-600">({service.totalReviews})</span>}
                        </div>
                      )}
                      {service.totalBookings > 0 && (
                        <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
                          <Briefcase className="w-4 h-4" />
                          <span>{service.totalBookings} jobs completed</span>
                        </div>
                      )}
                      {service.responseTime && (
                        <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
                          <Clock className="w-4 h-4" />
                          <span>Responds in {service.responseTime}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {service.subcategory && (
                    <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-sm font-semibold">
                      {service.subcategory}
                    </span>
                  )}
                  {service.mainCategory && (
                    <span className="px-3 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full text-sm font-semibold">
                      {service.mainCategory.replace(/-/g, ' ')}
                    </span>
                  )}
                  {service.status === 'active' && (
                    <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-sm font-semibold">
                      Available
                    </span>
                  )}
                </div>
              </div>

              {/* About */}
              <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 p-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">About This Service</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  {service.description || 'No description provided.'}
                </p>
              </div>

              {/* Skills */}
              {service.skills && service.skills.length > 0 && (
                <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 p-6">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Skills & Specializations</h2>
                  <div className="flex flex-wrap gap-2">
                    {service.skills.map((skill, index) => (
                      <span key={index} className="inline-flex items-center px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300">
                        <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Availability */}
              {service.availability && service.availability.length > 0 && (
                <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 p-6">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Availability</h2>
                  <div className="flex flex-wrap gap-2">
                    {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map((day) => (
                      <span key={day} className={`px-4 py-2 rounded-xl text-sm font-semibold ${
                        service.availability.includes(day)
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600'
                      }`}>
                        {day.slice(0, 3)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Booking Card */}
            <div className="lg:col-span-1">
              <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 p-6 sticky top-24">
                
                {/* Price */}
                <div className="mb-6">
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-3xl font-black text-gray-900 dark:text-white">
                      LKR {service.price?.toLocaleString()}
                    </span>
                    <span className="text-gray-600 dark:text-gray-400">/ {service.priceType}</span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Starting price</p>
                </div>

                {/* Details */}
                <div className="space-y-3 mb-6">
                  {service.experience && (
                    <div className="flex items-start gap-3">
                      <Award className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">Experience</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{service.experience}</p>
                      </div>
                    </div>
                  )}
                  {service.location && (
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">Service Area</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{service.location}</p>
                      </div>
                    </div>
                  )}
                  {service.responseTime && (
                    <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">Response Time</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{service.responseTime}</p>
                      </div>
                    </div>
                  )}
                  {service.availability && service.availability.length > 0 && (
                    <div className="flex items-start gap-3">
                      <Calendar className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">Availability</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {service.availability.slice(0, 3).join(', ')}
                          {service.availability.length > 3 && ` +${service.availability.length - 3} more`}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* CTA Buttons */}
                <button onClick={() => setShowContactModal(true)}
                  className="w-full py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white font-bold rounded-xl hover:shadow-2xl hover:shadow-yellow-400/30 transition-all mb-3 flex items-center justify-center gap-2"
                >
                  <MessageSquare className="w-5 h-5" />
                  Contact Provider
                </button>

                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 space-y-2">
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Verified Provider</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>100% Response Rate</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-md">
          <div className="relative w-full max-w-md bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 p-6">
            <button onClick={() => setShowContactModal(false)} className="absolute top-4 right-4 p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-all">
              <X className="w-5 h-5 text-gray-500" />
            </button>

            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Contact {providerName}</h2>

            <div className="space-y-3">
              {providerPhone && (
                <a href={`tel:${providerPhone}`} className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-all">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                    <Phone className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Phone</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{providerPhone}</p>
                  </div>
                </a>
              )}

              {providerEmail && (
                <a href={`mailto:${providerEmail}`} className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-all">
                  <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                    <Mail className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Email</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{providerEmail}</p>
                  </div>
                </a>
              )}

              {providerPhone && (
                <a href={`https://wa.me/${providerPhone.replace(/[^0-9]/g, '')}?text=Hi!%20I%20found%20your%20service%20on%20HIVE`}
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 p-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl hover:shadow-xl transition-all"
                >
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <MessageSquare className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-white/80">Chat on WhatsApp</p>
                    <p className="text-sm font-bold">Send Message Now</p>
                  </div>
                </a>
              )}

              {!providerPhone && !providerEmail && (
                <p className="text-center text-gray-500 dark:text-gray-400 py-4">No contact information available.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}